// ========== نقش‌ها و دسترسی‌های رابط کاربری ==========
// role_id از GET /auth/me (و در ابتدای کار از payload توکن) خوانده می‌شود.
//
// هشدار: این فایل فقط رابط کاربری را کنترل می‌کند و مرجع مجوز نیست. کنترل واقعی
// دسترسی همیشه سمت سرور است؛ اگر سرور 403 بدهد باید پیام عدم دسترسی نمایش داده شود.

export const ROLE = {
  SUPER_ADMIN: 0,
  ADMIN: 1,
  SALES_EXPERT: 2,
  ACCOUNTING_EXPERT: 3,
  SALES_MANAGER: 4,
  PRODUCTION_EXPERT: 5,
  WAREHOUSE_KEEPER: 6,
  SECURITY_GUARD: 7,
  LOADING_OPERATOR: 8,
} as const;

export type RoleId = (typeof ROLE)[keyof typeof ROLE];

export const ROLE_LABELS: Record<number, string> = {
  [ROLE.SUPER_ADMIN]: "سوپرادمین",
  [ROLE.ADMIN]: "ادمین",
  [ROLE.SALES_EXPERT]: "کارشناس فروش",
  [ROLE.ACCOUNTING_EXPERT]: "کارشناس حسابداری",
  [ROLE.SALES_MANAGER]: "مدیر فروش",
  [ROLE.PRODUCTION_EXPERT]: "کارشناس تولید",
  [ROLE.WAREHOUSE_KEEPER]: "انباردار",
  [ROLE.SECURITY_GUARD]: "حراست",
  [ROLE.LOADING_OPERATOR]: "کاربر بارگیری",
};

export const isAdminRole = (roleId?: number | null): boolean =>
  roleId === ROLE.SUPER_ADMIN || roleId === ROLE.ADMIN;

/** ادمین و سوپرادمین همیشه مجازند؛ بقیه فقط اگر در فهرست نقش‌های مجاز باشند. */
export const allowRoles = (roleId: number | null | undefined, roles: readonly number[]): boolean => {
  if (roleId === null || roleId === undefined) return false;
  return isAdminRole(roleId) || roles.indexOf(roleId) !== -1;
};

// ========== بخش‌ها (منو و مسیرها) ==========

export type SectionKey =
  | "initialData"
  | "salesSubstitutions"
  | "minimumPayment"
  | "userManagement"
  | "allOrders"
  | "otherOrders"
  | "proformaInvoices"
  | "pendingPayments"
  | "confirmedPayments"
  | "salesAvailability"
  | "salesOrders"
  | "readyToProduceGoods"
  | "goodsInProduction"
  | "producedGoods"
  | "productsWarehouse"
  | "goodsExitedWarehouse"
  | "loadedGoods"
  | "deliveredGoods"
  | "ordersInProduction"
  | "producedOrders"
  | "ordersInWarehouse"
  | "ordersExitedWarehouse"
  | "loadedOrders"
  | "deliveredOrders";

// نقش‌های غیرادمینی که هر بخش را می‌بینند. ادمین/سوپرادمین همه‌ی بخش‌ها را می‌بینند.
export const SECTION_ROLES: Record<SectionKey, readonly number[]> = {
  // کارشناس فروش و مدیر فروش به داده‌های اولیه و همه‌ی عملیات آن دسترسی دارند،
  // به‌جز «حداقل پرداختی» که بخش جداگانه‌ای است و فقط ادمین/سوپرادمین دارد.
  initialData: [ROLE.SALES_EXPERT, ROLE.SALES_MANAGER],
  // «تعیین جایگزین»: ادمین و سوپرادمین (همیشه) و مدیر فروش.
  salesSubstitutions: [ROLE.SALES_MANAGER],
  minimumPayment: [],
  userManagement: [],

  allOrders: [ROLE.SALES_EXPERT, ROLE.ACCOUNTING_EXPERT, ROLE.SALES_MANAGER],
  // «سایر سفارش‌ها» همان فهرست همه سفارش‌هاست، ولی فقط‌خواندنی و بدون محدودشدن به
  // رکوردهای خودِ کارشناس فروش. دسترسی‌اش عیناً مثل «همه سفارش‌ها» است.
  otherOrders: [ROLE.SALES_EXPERT, ROLE.ACCOUNTING_EXPERT, ROLE.SALES_MANAGER],
  proformaInvoices: [ROLE.SALES_EXPERT, ROLE.SALES_MANAGER],
  pendingPayments: [ROLE.ACCOUNTING_EXPERT],
  confirmedPayments: [ROLE.ACCOUNTING_EXPERT],
  salesAvailability: [ROLE.SALES_MANAGER],
  salesOrders: [ROLE.SALES_EXPERT, ROLE.SALES_MANAGER],

  // وضعیت کالاها
  readyToProduceGoods: [ROLE.PRODUCTION_EXPERT],
  goodsInProduction: [ROLE.PRODUCTION_EXPERT],
  producedGoods: [ROLE.PRODUCTION_EXPERT, ROLE.WAREHOUSE_KEEPER],
  productsWarehouse: [ROLE.PRODUCTION_EXPERT, ROLE.WAREHOUSE_KEEPER],
  // طبق مستند نسخه ۵: انباردار، حراست و بارگیری «خارج‌شده از انبار» را می‌بینند؛
  // کارشناس فروش فقط به «بارگیری‌شده» و «تحویل‌شده» دسترسی دارد.
  goodsExitedWarehouse: [ROLE.WAREHOUSE_KEEPER, ROLE.SECURITY_GUARD, ROLE.LOADING_OPERATOR],
  loadedGoods: [ROLE.SALES_EXPERT],
  deliveredGoods: [ROLE.SALES_EXPERT],

  // وضعیت سفارش‌ها
  ordersInProduction: [ROLE.SALES_EXPERT, ROLE.SALES_MANAGER],
  producedOrders: [ROLE.SALES_EXPERT, ROLE.SALES_MANAGER],
  ordersInWarehouse: [ROLE.SALES_EXPERT, ROLE.SALES_MANAGER, ROLE.WAREHOUSE_KEEPER],
  ordersExitedWarehouse: [
    ROLE.SALES_EXPERT,
    ROLE.SALES_MANAGER,
    ROLE.WAREHOUSE_KEEPER,
    ROLE.SECURITY_GUARD,
  ],
  loadedOrders: [ROLE.SALES_EXPERT, ROLE.SALES_MANAGER],
  deliveredOrders: [ROLE.SALES_EXPERT, ROLE.SALES_MANAGER],
};

export const canViewSection = (roleId: number | null | undefined, section: SectionKey): boolean =>
  allowRoles(roleId, SECTION_ROLES[section]);

// ========== عملیات ==========

/** ویرایش و حذف پیش‌فاکتور (شرط‌های وضعیت و پرداخت جداگانه بررسی می‌شوند). */
export const canManageProforma = (roleId?: number | null) =>
  allowRoles(roleId, [ROLE.SALES_EXPERT]);

/**
 * ایجاد پیش‌فاکتور. عمداً از canManageProforma جداست چون مدیر فروش اجازه‌ی ایجاد
 * دارد ولی ویرایش و حذف پیش‌فاکتورها را ندارد.
 */
export const canCreateProforma = (roleId?: number | null) =>
  allowRoles(roleId, [ROLE.SALES_EXPERT, ROLE.SALES_MANAGER]);

/** ایجاد/ویرایش/حذف در صفحه‌های داده‌های اولیه (مشتریان، کالاها، ارزها، ...). */
export const canManageInitialData = (roleId?: number | null) =>
  allowRoles(roleId, [ROLE.SALES_EXPERT, ROLE.SALES_MANAGER]);

/** مدیریت فعال بودن فروش فقط برای مدیر فروش و مدیران سیستم. */
export const canManageSalesAvailability = (roleId?: number | null) =>
  allowRoles(roleId, [ROLE.SALES_MANAGER]);

/** ثبت پرداختی */
export const canRegisterPayment = (roleId?: number | null) =>
  allowRoles(roleId, [ROLE.SALES_EXPERT]);

/** تأیید پرداختی (تأیید حسابداری) */
export const canConfirmPaymentAccounting = (roleId?: number | null) =>
  allowRoles(roleId, [ROLE.ACCOUNTING_EXPERT]);

/** تأیید مدیر فروش روی پیش‌فاکتور */
export const canApproveSalesManager = (roleId?: number | null) =>
  allowRoles(roleId, [ROLE.SALES_MANAGER]);

/**
 * ثبت/ویرایش/حذف رابط. طبق مستند نسخه ۴، نقش‌های ۰ تا ۴ مجازند و نقش‌های تولید،
 * انبار و حراست فقط فهرست و جزئیات را می‌بینند.
 */
export const canManageContacts = (roleId?: number | null) =>
  allowRoles(roleId, [ROLE.SALES_EXPERT, ROLE.ACCOUNTING_EXPERT, ROLE.SALES_MANAGER]);

/**
 * نمایش فیلد نام مشتری. طبق مستند بک‌اند، برای کارشناس تولید و حراست فیلدهای
 * customer_id و customer_name اصلاً ارسال نمی‌شوند و نباید نمایش داده شوند.
 */
export const canSeeCustomerName = (roleId?: number | null): boolean =>
  roleId !== ROLE.PRODUCTION_EXPERT && roleId !== ROLE.SECURITY_GUARD;

/** در صفحه‌های کارشناس تولید و حراست نباید فیلتر customer_id ارسال شود. */
export const canFilterByCustomer = canSeeCustomerName;

/**
 * نگاشت نام انگلیسی نقش (همان مقداری که GET /roles/ و GET /auth/me می‌دهند) به برچسب
 * فارسی. صفحه‌ی مدیریت کاربران از این استفاده می‌کند تا با بقیه‌ی اپ یکی بماند.
 */
export const ROLE_LABEL_BY_NAME: Record<string, string> = {
  superadmin: ROLE_LABELS[ROLE.SUPER_ADMIN],
  admin: ROLE_LABELS[ROLE.ADMIN],
  sales_expert: ROLE_LABELS[ROLE.SALES_EXPERT],
  accounting_expert: ROLE_LABELS[ROLE.ACCOUNTING_EXPERT],
  sales_manager: ROLE_LABELS[ROLE.SALES_MANAGER],
  production_expert: ROLE_LABELS[ROLE.PRODUCTION_EXPERT],
  warehouse_keeper: ROLE_LABELS[ROLE.WAREHOUSE_KEEPER],
  security_guard: ROLE_LABELS[ROLE.SECURITY_GUARD],
  loading_operator: ROLE_LABELS[ROLE.LOADING_OPERATOR],
};

/**
 * مسیر هر بخش. هم روت‌ها و هم صفحه‌ی فرود پس از ورود از همین نگاشت تغذیه می‌شوند
 * تا با منو ناهمگام نشوند.
 */
export const SECTION_ROUTES: Record<SectionKey, string> = {
  initialData: "/ProvincialRegistration",
  salesSubstitutions: "/SalesSubstitutions",
  minimumPayment: "/MinimumPaymentSettings",
  userManagement: "/manageUser",
  proformaInvoices: "/ProformaInvoices",
  pendingPayments: "/PendingPayments",
  confirmedPayments: "/ConfirmedPayments",
  salesAvailability: "/SalesAvailability",
  allOrders: "/AllOrders",
  otherOrders: "/OtherOrders",
  salesOrders: "/SalesOrders",
  readyToProduceGoods: "/ReadyToProduceGoods",
  goodsInProduction: "/GoodsInProduction",
  producedGoods: "/ProducedGoods",
  productsWarehouse: "/ProductsWarehouse",
  goodsExitedWarehouse: "/GoodsExitedWarehouse",
  loadedGoods: "/LoadedGoods",
  deliveredGoods: "/DeliveredGoods",
  ordersInProduction: "/OrdersInProduction",
  producedOrders: "/ProducedOrders",
  ordersInWarehouse: "/OrdersInWarehouse",
  ordersExitedWarehouse: "/OrdersExitedWarehouse",
  loadedOrders: "/LoadedOrders",
  deliveredOrders: "/DeliveredOrders",
};

// ترتیب اولویت صفحه‌ی فرود؛ اولین بخشی که نقش اجازه‌اش را دارد انتخاب می‌شود.
// مثلاً کارشناس حسابداری پیش‌فاکتورها را ندارد، پس به «همه سفارش‌ها» می‌رود و
// کارشناس تولید به «کالاهای آماده تولید».
const LANDING_PRIORITY: readonly SectionKey[] = [
  "proformaInvoices",
  "pendingPayments",
  "confirmedPayments",
  "allOrders",
  "otherOrders",
  "salesOrders",
  "readyToProduceGoods",
  "producedGoods",
  "ordersInWarehouse",
  "ordersExitedWarehouse",
  "productsWarehouse",
  "goodsExitedWarehouse",
  "loadedGoods",
  "deliveredGoods",
  "goodsInProduction",
  "ordersInProduction",
  "producedOrders",
  "loadedOrders",
  "deliveredOrders",
  "userManagement",
  "initialData",
  "salesSubstitutions",
  "minimumPayment",
];

/**
 * صفحه‌ای که کاربر پس از ورود باید به آن برود. اگر نقش قابل تشخیص نباشد یا هیچ
 * بخشی نداشته باشد، به پروفایل می‌رود تا به‌جای صفحه‌ی «یافت نشد» گیر نکند.
 */
export const getLandingRoute = (roleId?: number | null): string => {
  const section = LANDING_PRIORITY.find((s) => canViewSection(roleId, s));
  return section ? SECTION_ROUTES[section] : "/user-profile";
};
